<script setup>
import { ref, computed, watch } from 'vue'
import { useStore } from 'vuex'
import {
  CheckCircle,
  AlertTriangle,
  XCircle,
  Eye,
  Trash2,
  MapPin,
  FileText,
  Map,
  Edit2
} from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import FormFeature from '@/components/forms/FormFeature.vue'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableEmpty
} from '@/components/ui/table'

const props = defineProps({
  features: {
    type: Array,
    required: true
  },
  isUploading: {
    type: Boolean,
    default: false
  },
  selectedProject: {
    type: Object,
    default: null
  }
})

const store = useStore()
const allTemplates = computed(() => store.getters['geo/getAllTemplates'] || [])

const emit = defineEmits([
  'preview-feature',
  'edit-feature',
  'delete-feature',
  'bulk-edit',
  'upload-selected',
  'validate-feature',
  'update-feature',
  'feature-uploaded'
])

const sortBy = ref('name')
const sortOrder = ref('asc')
const expandedRows = ref(new Set())



// Computed properties
const filteredFeatures = computed(() => {
  // Handle empty or invalid features array
  if (!props.features || !Array.isArray(props.features) || props.features.length === 0) {
    return []
  }

  let filtered = [...props.features]

  // Sort features
  filtered.sort((a, b) => {
    let aValue, bValue

    switch (sortBy.value) {
      case 'name':
        aValue = a.properties?.name || a.properties?.fileName || ''
        bValue = b.properties?.name || b.properties?.fileName || ''
        // Use string comparison
        if (sortOrder.value === 'asc') {
          return aValue.localeCompare(bValue)
        } else {
          return bValue.localeCompare(aValue)
        }
      case 'type':
        aValue = a.geometry?.type || ''
        bValue = b.geometry?.type || ''
        // Use string comparison
        if (sortOrder.value === 'asc') {
          return aValue.localeCompare(bValue)
        } else {
          return bValue.localeCompare(aValue)
        }
      case 'file':
        aValue = a.properties?.fileName || ''
        bValue = b.properties?.fileName || ''
        // Use string comparison
        if (sortOrder.value === 'asc') {
          return aValue.localeCompare(bValue)
        } else {
          return bValue.localeCompare(aValue)
        }
      case 'status':
        aValue = getValidationStatus(a).priority
        bValue = getValidationStatus(b).priority
        // Use numeric comparison
        if (sortOrder.value === 'asc') {
          return aValue - bValue
        } else {
          return bValue - aValue
        }
      default:
        return 0
    }
  })

  return filtered
})

const validFeatures = computed(() => {
  return filteredFeatures.value.filter(feature =>
    getValidationStatus(feature).status === 'valid'
  )
})

const visibleFeatures = computed(() => {
  return filteredFeatures.value.map((feature, index) => ({ feature, index }))
})




// Methods
const getValidationStatus = (feature) => {
  const issues = []
  const warnings = []

  // Check if feature exists and has basic structure
  if (!feature) {
    issues.push('Invalid feature structure')
    return { status: 'error', icon: XCircle, class: 'text-red-500', priority: 2, issues }
  }

  // Check geometry (required for valid feature)
  if (!feature.geometry || !feature.geometry.type) {
    issues.push('Missing geometry')
  } else {
    // Check coordinates for different geometry types
    if (feature.geometry.type === 'Point') {
      const coords = feature.geometry.coordinates
      if (!coords || coords.length < 2 ||
          coords[0] === null || coords[1] === null ||
          Math.abs(coords[0]) > 180 || Math.abs(coords[1]) > 90) {
        issues.push('Invalid coordinates')
      }
    } else if (feature.geometry.type === 'LineString') {
      const coords = feature.geometry.coordinates
      if (!coords || coords.length < 2) {
        issues.push('LineString requires at least 2 points')
      }
    } else if (feature.geometry.type === 'Polygon') {
      const coords = feature.geometry.coordinates
      if (!coords || coords.length === 0 || coords[0].length < 4) {
        issues.push('Polygon requires at least 4 points')
      }
    }
  }

  // Check for properties (optional - features can have null properties)
  const hasProperties = feature.properties && typeof feature.properties === 'object'

  // Check name (optional - only warning if missing when properties exist)
  if (hasProperties && !feature.properties.name && !feature.properties.fileName) {
    warnings.push('Missing name')
  }

  // Add info about description if present
  const hasDescription = hasProperties && feature.properties.description &&
    typeof feature.properties.description === 'string' &&
    feature.properties.description.trim() !== ''

  // Determine status based on issues
  if (issues.length === 0) {
    let statusText = 'Valid'
    if (hasDescription) {
      statusText = 'Valid (with description)'
    } else if (hasProperties) {
      statusText = 'Valid (with properties)'
    } else {
      statusText = 'Valid (geometry only)'
    }

    return {
      status: 'valid',
      icon: CheckCircle,
      class: 'text-green-500',
      priority: 0,
      statusText,
      warnings
    }
  } else if (issues.some(issue => issue.includes('geometry') || issue.includes('coordinates') || issue.includes('points'))) {
    return { status: 'error', icon: XCircle, class: 'text-red-500', priority: 2, issues, warnings }
  } else {
    return { status: 'warning', icon: AlertTriangle, class: 'text-yellow-500', priority: 1, issues, warnings }
  }
}

const getGeometryIcon = (type) => {
  switch (type) {
    case 'Point': return MapPin
    case 'LineString': return Edit2
    case 'Polygon': return Map
    default: return FileText
  }
}



const handleSort = (field) => {
  if (sortBy.value === field) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortBy.value = field
    sortOrder.value = 'asc'
  }
}

const getSortIcon = (field) => {
  if (sortBy.value !== field) return ''
  return sortOrder.value === 'asc' ? '↑' : '↓'
}




// Row expansion methods
const toggleRowExpansion = (feature, index) => {
  const key = getFeatureKey(feature, index)
  if (expandedRows.value.has(key)) {
    expandedRows.value.delete(key)
  } else {
    expandedRows.value.add(key)
  }
}

const isRowExpanded = (feature, index) => {
  const key = getFeatureKey(feature, index)
  return expandedRows.value.has(key)
}

const formatFeatureData = (feature) => {
  const hasProperties = feature.properties && typeof feature.properties === 'object'
  const validationStatus = getValidationStatus(feature)

  const data = {}

  // Only show basic information
  data['Details'] = {
    'Name': hasProperties ? (feature.properties.name || feature.properties.fileName || `Unnamed ${feature.geometry?.type || 'Feature'}`) : `Unnamed ${feature.geometry?.type || 'Feature'}`,
    'Type': feature.geometry?.type || 'Unknown',
    'Coordinates': formatGeometryCoordinates(feature.geometry)
  }

  // Only add description if it exists and is not empty
  if (hasProperties && feature.properties.description && feature.properties.description.trim() !== '') {
    data['Details']['Description'] = feature.properties.description
  }

  // Only show validation errors if they exist
  if (validationStatus.issues && validationStatus.issues.length > 0) {
    data['Errors'] = {
      'Issues': validationStatus.issues.join(', ')
    }
  }

  return data
}

const formatGeometryCoordinates = (geometry) => {
  if (!geometry || !geometry.coordinates) return 'N/A'

  switch (geometry.type) {
    case 'Point':
      return `[${geometry.coordinates[0].toFixed(6)}, ${geometry.coordinates[1].toFixed(6)}]`
    case 'LineString':
      return `${geometry.coordinates.length} points`
    case 'Polygon':
      return `${geometry.coordinates[0].length} points (outer ring)`
    case 'MultiPoint':
      return `${geometry.coordinates.length} points`
    case 'MultiLineString':
      return `${geometry.coordinates.length} lines`
    case 'MultiPolygon':
      return `${geometry.coordinates.length} polygons`
    case 'GeometryCollection':
      return `${geometry.geometries.length} geometries`
    default:
      return 'Complex geometry'
  }
}

const getCoordinateCount = (geometry) => {
  if (!geometry || !geometry.coordinates) return 0

  switch (geometry.type) {
    case 'Point':
      return 1
    case 'LineString':
      return geometry.coordinates.length
    case 'Polygon':
      return geometry.coordinates.reduce((sum, ring) => sum + ring.length, 0)
    case 'MultiPoint':
      return geometry.coordinates.length
    case 'MultiLineString':
      return geometry.coordinates.reduce((sum, line) => sum + line.length, 0)
    case 'MultiPolygon':
      return geometry.coordinates.reduce((sum, polygon) =>
        sum + polygon.reduce((polySum, ring) => polySum + ring.length, 0), 0
      )
    case 'GeometryCollection':
      return geometry.geometries.reduce((sum, geom) => sum + getCoordinateCount(geom), 0)
    default:
      return 0
  }
}

// Helper function to get a unique key for a feature
const getFeatureKey = (feature, index) => {
  if (feature.properties?.local_uuid) {
    return feature.properties.local_uuid
  }

  // For features without properties, create a key based on geometry and index
  if (feature.geometry) {
    const geomType = feature.geometry.type
    const coords = JSON.stringify(feature.geometry.coordinates)
    return `${geomType}-${index}-${coords.substring(0, 50)}`
  }

  return `feature-${index}`
}

// Prepare feature for FormFeature component
const prepareFeatureForForm = (feature) => {
  // Ensure feature has proper structure for FormFeature
  const prepared = {
    type: 'Feature',
    geometry: feature.geometry,
    properties: {
      ...feature.properties,
      // Ensure required fields have defaults
      name: feature.properties?.name || feature.properties?.fileName?.split('.').shift() || 'Unnamed Feature',
      description: feature.properties?.description || '',
      color: feature.properties?.color || '#0059ff',
      shared: feature.properties?.shared || false,
      is_static: feature.properties?.is_static || false,
      created_origin: feature.properties?.created_origin || Date.now(),
      // Pre-select the project if one is selected at the top
      project_id: props.selectedProject?.project_id || feature.properties?.project_id,
      isUpload: false // Set to trigger upload when needed
    }
  }
  return prepared
}

// Handle feature creation from form
const handleFeatureCreated = (originalFeature, createdFeature) => {
  // Remove the original feature from the list after successful upload
  const index = filteredFeatures.value.findIndex((f, idx) =>
    getFeatureKey(f, idx) === getFeatureKey(originalFeature, idx)
  )
  if (index !== -1) {
    emit('feature-uploaded', originalFeature, createdFeature)
  }
  // Collapse the row after successful upload
  const key = getFeatureKey(originalFeature, index)
  expandedRows.value.delete(key)
}

const handleFeatureSaved = (originalFeature, savedFeature) => {
  // Handle feature save (for edit mode, if needed)
}

const handleFeatureUpdate = (originalFeature, updatedFeature) => {
  // Handle feature update during editing
  Object.assign(originalFeature, updatedFeature)

  // Emit update to parent to update the map
  emit('update-feature', originalFeature)
}

// Watch for changes
watch(() => props.features, () => {
  // Features changed, no need to clear selection since we removed it
}, { deep: true })


</script>

<template>
  <div class="space-y-4">
    <!-- Header with stats -->
    <div class="flex items-center justify-between">
      <div class="flex items-center space-x-4">
        <div class="text-sm text-gray-600">
          {{ filteredFeatures.length }} {{ filteredFeatures.length === 1 ? 'feature' : 'features' }}
        </div>
      </div>

      <div class="flex items-center space-x-2">
        <Button
          v-if="validFeatures.length > 0"
          @click="emit('upload-selected', validFeatures)"
          size="sm"
          :disabled="isUploading || !selectedProject"
          :title="!selectedProject ? 'Select a project first' : ''"
        >
          Upload All ({{ validFeatures.length }})
        </Button>
        <div v-if="!selectedProject" class="text-sm text-yellow-600">
          Select a project to enable upload
        </div>
      </div>
    </div>


    <!-- Summary badges - only show errors if present -->
    <div v-if="filteredFeatures.filter(f => getValidationStatus(f).status === 'error').length > 0" class="flex items-center space-x-2">
      <Badge variant="secondary" class="text-red-600">
        <XCircle class="w-3 h-3 mr-1" />
        {{ filteredFeatures.filter(f => getValidationStatus(f).status === 'error').length }} Errors
      </Badge>
    </div>

            <!-- Table -->
    <Table class="border rounded-lg">
      <TableHeader>
        <TableRow>
          <TableHead
            class="cursor-pointer hover:bg-gray-100 px-2 py-1 rounded"
            @click="handleSort('name')"
          >
            Name {{ getSortIcon('name') }}
          </TableHead>
          <TableHead
            class="cursor-pointer hover:bg-gray-100 px-2 py-1 rounded"
            @click="handleSort('type')"
          >
            Type {{ getSortIcon('type') }}
          </TableHead>
          <TableHead></TableHead>
        </TableRow>
      </TableHeader>

      <TableBody>
        <template v-if="filteredFeatures.length > 0">
          <template v-for="{ feature, index } in visibleFeatures" :key="getFeatureKey(feature, index)">
            <TableRow class="hover:bg-gray-50">
              <!-- Name -->
              <TableCell class="min-w-0">
                <div class="font-medium truncate max-w-40">
                  {{
                    feature.properties?.name ||
                    feature.properties?.fileName?.split('.').shift() ||
                    feature.geometry?.type ||
                    'Feature'
                  }}
                </div>
              </TableCell>

              <!-- Type -->
              <TableCell>
                <div class="flex items-center space-x-2">
                  <component
                    v-if="getValidationStatus(feature).status === 'error'"
                    :is="getValidationStatus(feature).icon"
                    :class="getValidationStatus(feature).class"
                    class="w-3.5 h-3.5"
                    :title="getValidationStatus(feature).issues?.join(', ')"
                  />
                  <span class="text-sm">{{ feature.geometry?.type || 'Unknown' }}</span>
                </div>
              </TableCell>


              <!-- Actions -->
              <TableCell>
                <div class="flex items-center space-x-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    @click="emit('preview-feature', feature)"
                    class="h-8 w-8 p-0"
                    title="Preview on map"
                  >
                    <Eye class="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    @click="emit('delete-feature', feature)"
                    class="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                    title="Delete feature"
                  >
                    <Trash2 class="w-4 h-4" />
                  </Button>

                  <Button
                    size="sm"
                    variant="ghost"
                    @click="toggleRowExpansion(feature, index)"
                    class="h-8 w-8 p-0"
                    :title="isRowExpanded(feature, index) ? 'Close editor' : 'Edit feature'"
                  >
                    <Edit2 class="w-4 h-4" :class="isRowExpanded(feature, index) ? 'text-blue-600' : ''" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>

            <!-- Expanded Details Row with Form -->
            <TableRow
              v-if="isRowExpanded(feature, index)"
              class="bg-gray-50 border-t"
            >
              <TableCell colspan="4" class="p-4">
                <div class="max-w-2xl">
                  <FormFeature
                    :key="`${getFeatureKey(feature, index)}-${props.selectedProject?.project_id || 'no-project'}`"
                    :feature="prepareFeatureForForm(feature)"
                    :predefined-project-id="props.selectedProject?.project_id?.toString()"
                    :hide-close="true"
                    @update:created="handleFeatureCreated(feature, $event)"
                    @update:saved="handleFeatureSaved(feature, $event)"
                    @update:feature="handleFeatureUpdate(feature, $event)"
                  />
                </div>
              </TableCell>
            </TableRow>
          </template>
        </template>

        <!-- Empty state -->
        <TableEmpty v-else colspan="4">
          <div class="text-center py-12">
            <FileText class="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 class="text-lg font-medium text-gray-900 mb-2">No features found</h3>
            <p class="text-gray-500">
              Import some files to get started.
            </p>
          </div>
        </TableEmpty>
      </TableBody>
    </Table>

  </div>
</template>
